import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, GaugeCircle, Layers, Users as UsersIcon } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";

const TYPES = [
  "purchase", "expense", "leave", "transfer",
  "hiring", "admin_request", "other",
];

const EMPTY_RULE = { max_amount: "", approvers: [""], label: "" };

function humanAmount(v) {
  if (v === null || v === "" || v === undefined) return "∞ (no ceiling)";
  return `≤ UGX ${Number(v).toLocaleString()}`;
}

function RuleEditor({ rule, idx, users, onChange, onRemove, canRemove, currentUserId }) {
  const setField = (k, v) => onChange({ ...rule, [k]: v });
  return (
    <div className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50/50"
      data-testid={`policy-rule-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
          Tier {idx + 1}
        </div>
        {canRemove && (
          <Button type="button" variant="ghost" size="sm"
            onClick={onRemove}
            className="h-7 text-red-700 hover:bg-red-50"
            data-testid={`policy-rule-remove-${idx}`}>
            <Trash2 size={12} />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Max amount (UGX)</Label>
          <Input type="number" value={rule.max_amount ?? ""}
            onChange={(e) => setField("max_amount", e.target.value)}
            placeholder="Leave empty = no ceiling"
            className="mt-1 h-9" data-testid={`policy-rule-max-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">Label (optional)</Label>
          <Input value={rule.label || ""}
            onChange={(e) => setField("label", e.target.value)}
            placeholder='e.g. "Branch Manager only"'
            className="mt-1 h-9" data-testid={`policy-rule-label-${idx}`} />
        </div>
      </div>
      <div>
        <Label className="text-xs">Approvers (in order)</Label>
        <div className="space-y-1.5 mt-1">
          {rule.approvers.map((aid, i) => (
            <div key={i} className="flex gap-1.5">
              <Select value={aid} onValueChange={(v) => {
                const next = [...rule.approvers]; next[i] = v; setField("approvers", next);
              }}>
                <SelectTrigger className="h-9" data-testid={`policy-rule-${idx}-approver-${i}`}>
                  <SelectValue placeholder={`Approver ${i + 1}`} />
                </SelectTrigger>
                <SelectContent>
                  {users.filter((u) => u.id !== currentUserId).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} · {u.role?.replaceAll("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rule.approvers.length > 1 && (
                <Button type="button" variant="outline" size="sm"
                  onClick={() => {
                    const next = rule.approvers.filter((_, j) => j !== i);
                    setField("approvers", next);
                  }}
                  className="h-9 text-red-700">
                  <Trash2 size={12} />
                </Button>
              )}
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm"
            onClick={() => setField("approvers", [...rule.approvers, ""])}
            data-testid={`policy-rule-${idx}-add-approver`}>
            + Add approver
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ApprovalPolicies() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // policy being edited
  const [form, setForm] = useState({
    type: "purchase", branch: "__all__",
    rules: [{ ...EMPTY_RULE, approvers: [""] }],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: u }, { data: b }] = await Promise.all([
        api.get("/approval-policies"), api.get("/users"), api.get("/branches"),
      ]);
      setItems(p); setUsers(u); setBranches(b);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ type: "purchase", branch: "__all__", rules: [{ ...EMPTY_RULE, approvers: [""] }] });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      type: p.type,
      branch: p.branch || "__all__",
      rules: (p.rules || []).map((r) => ({
        max_amount: r.max_amount ?? "",
        approvers: r.approvers?.length ? [...r.approvers] : [""],
        label: r.label || "",
      })),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        type: form.type,
        branch: form.branch === "__all__" ? null : form.branch,
        rules: form.rules.map((r) => ({
          max_amount: r.max_amount === "" || r.max_amount === null ? null : Number(r.max_amount),
          approvers: r.approvers.filter(Boolean),
          label: r.label || null,
        })).filter((r) => r.approvers.length > 0),
      };
      if (!payload.rules.length) {
        toast.error("Add at least one tier with an approver.");
        setBusy(false); return;
      }
      if (editing) {
        await api.patch(`/approval-policies/${editing.id}`, payload);
        toast.success("Policy updated");
      } else {
        await api.post("/approval-policies", payload);
        toast.success("Policy created");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the '${p.type}' policy${p.branch ? ` for ${p.branch}` : ""}?`)) return;
    try { await api.delete(`/approval-policies/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setRule = (i, r) => {
    const next = [...form.rules]; next[i] = r; setForm({ ...form, rules: next });
  };
  const addRule = () => setForm({ ...form, rules: [...form.rules, { ...EMPTY_RULE, approvers: [""] }] });
  const removeRule = (i) => setForm({ ...form, rules: form.rules.filter((_, j) => j !== i) });

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Approval Thresholds"
        subtitle="Set amount-based tiers per request type. When someone submits an approval, the correct approvers are auto-selected based on amount and branch."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white"
                onClick={openNew}
                data-testid="policy-new-button">
                <Plus size={16} className="mr-1.5" /> New policy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <GaugeCircle size={16} />
                  {editing ? "Edit policy" : "New approval policy"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="policy-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Request type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="mt-1" data-testid="policy-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TYPES.map((t) => (<SelectItem key={t} value={t}>{t.replaceAll("_", " ")}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Branch scope</Label>
                    <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                      <SelectTrigger className="mt-1" data-testid="policy-branch-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All branches (default)</SelectItem>
                        {branches.map((b) => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2.5">
                  {form.rules.map((r, i) => (
                    <RuleEditor key={i} rule={r} idx={i} users={users}
                      onChange={(nr) => setRule(i, nr)}
                      onRemove={() => removeRule(i)}
                      canRemove={form.rules.length > 1}
                      currentUserId={null} />
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addRule}
                    className="w-full" data-testid="policy-add-rule">
                    <Plus size={12} className="mr-1" /> Add another tier
                  </Button>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="policy-submit">
                    {busy ? "Saving…" : (editing ? "Save changes" : "Create policy")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No policies configured"
          hint="Create a policy to automate approver selection based on amount." />
      ) : (
        <div className="space-y-3" data-testid="policies-list">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-gray-200 rounded-md p-5"
              data-testid={`policy-row-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge tone="emerald">{p.type?.replaceAll("_", " ")}</Badge>
                    {p.branch
                      ? <Badge tone="amber">{p.branch}</Badge>
                      : <Badge tone="slate">All branches</Badge>}
                    <span className="text-[11px] text-gray-500">
                      · {p.rules?.length || 0} tier(s)
                    </span>
                  </div>
                  <div className="space-y-1.5 mt-2">
                    {(p.rules || []).map((r, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <Layers size={14} className="text-emerald-800 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-900 font-medium">
                            {humanAmount(r.max_amount)}
                            {r.label && <span className="text-gray-500 font-normal"> · {r.label}</span>}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {(r.approvers || []).map((aid, j) => {
                              const u = users.find((u) => u.id === aid);
                              return (
                                <span key={j} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-700">
                                  <UsersIcon size={9} className="inline mr-1" />
                                  {u?.name || "unknown"}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)}
                    data-testid={`policy-edit-${p.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)}
                    className="text-red-700 hover:bg-red-50"
                    data-testid={`policy-delete-${p.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
